import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Avatar,
  Badge,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import BoltIcon from '@mui/icons-material/Bolt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useUser } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';

// Default quick prompts shown in carousel
const quickPrompts = [
  'How do I set up a Prayer Closet initiative?',
  'How do I approve or activate new users?',
  'What is Share & Accept and how does it work?',
  'How do I complete the Admin Agreement?',
  'How do I switch organizations?',
  'How do I add a new Sunday live stream?',
  'How do I add worship songs and chord sheets?',
  'How do I schedule daily devotions?',
];

// Rich, instant offline knowledge engine for GPN
const GPN_KNOWLEDGE_BASE = [
  {
    id: 'prayer_closet',
    keywords: ['prayer closet', 'war room', 'special prayer', 'special prayers', 'initiative', 'initiatives', 'campaign', 'prayer campaign', 'subscriber', 'subscribers', 'intercessor', 'intercessors'],
    title: 'Prayer Closet & War Room Initiatives',
    text: `**Prayer Closet** allows you to mobilize structured prayer campaigns across your church and community.

**Key Steps:**
1. Navigate to **Prayer Closet** in the left menu.
2. Click **Add Special Prayer** to configure the campaign title, dates, custom Scripture readings, and prayer focus.
3. Switch to the **Subscribers** tab to monitor enrolled intercessors and prayer commitments.
4. Mobile app members receive daily reminders to intercede during the active campaign.`,
    action: { label: 'Go to Prayer Closet', path: '/admin/prayer-closet' },
    followUps: ['How do I approve or activate new users?', 'What is Share & Accept and how does it work?']
  },
  {
    id: 'users',
    keywords: ['approve', 'approved', 'approving', 'user', 'users', 'member', 'members', 'registration', 'registrations', 'activate', 'activating', 'activation', 'deactivate', 'role', 'roles', 'permission', 'permissions', 'pending'],
    title: 'User Management & Approvals',
    text: `To manage, approve, or activate church members and administrators:

**Key Steps:**
1. Open **Users** in the sidebar.
2. Filter the roster by **Pending** to view new member registrations.
3. Review their name, contact email, and submitted church profile.
4. Click the **Activate / Approve** button or assign their role (**Church Admin**, **Moderator**, or **Member**).
5. You can also assign users to specific organizational prayer groups.`,
    action: { label: 'Go to Users', path: '/admin/users' },
    followUps: ['How do I complete the Admin Agreement?', 'How do I switch organizations?']
  },
  {
    id: 'admin_agreement',
    keywords: ['agreement', 'admin agreement', 'covenant', 'compliance', 'signature', 'sign', 'tracker', 'super admin agreement', 'alert', 'warning', 'certificate', 'accept agreement'],
    title: 'Admin Agreement & Compliance',
    text: `The **Admin Agreement** ensures ministerial confidentiality, adherence to the GPN Statement of Faith, and ethical stewardship.

**Key Points:**
• **Super Admins** are automatically exempt and have access to the **Compliance Oversight Tracker** to monitor all organization leaders (Accepted vs Pending).
• **Organization Admins** can review the covenant terms, enter their digital signature, and submit.
• Once accepted, an electronic verification certificate is issued and the warning alert is instantly cleared.`,
    action: { label: 'Open Admin Agreement', path: '/admin/admin-agreement' },
    followUps: ['How do I approve or activate new users?', 'Where is the Visual Guide?']
  },
  {
    id: 'share_and_accept',
    keywords: ['share & accept', 'share and accept', 'share', 'accept', 'generosity', 'benevolence', 'giver', 'items', 'donation', 'donate', 'item', 'furniture', 'clothing', 'books', 'provisions', 'need', 'needs'],
    title: 'Share & Accept Benevolence Ministry',
    text: `**Share & Accept** is GPN's Christian benevolence hub for sharing resources among believers:

**Management Features:**
• **Giver Items**: Review items donated by members (furniture, electronics, books, baby supplies, provisions).
• **Requests In Need**: Moderate requests from families or community members in financial need.
• **Generosity Testimonies**: Approve heartwarming praise reports when benevolence needs are met.`,
    action: { label: 'Go to Share & Accept', path: '/admin/share-and-accept' },
    followUps: ['How do I coordinate Ride Connect?', 'How do I set up a Prayer Closet initiative?']
  },
  {
    id: 'ride_connect',
    keywords: ['ride', 'rides', 'ride connect', 'driver', 'drivers', 'transport', 'transportation', 'shuttle', 'carpool', 'pickup', 'shuttles'],
    title: 'Ride Connect & Transportation',
    text: `**Ride Connect** facilitates transportation to church worship services, prayer vigils, and church conferences:

**Capabilities:**
1. Open **Ride Connect** from the left navigation.
2. Review registered **Volunteer Drivers** (verification & vehicle capacity).
3. Coordinate incoming member ride requests for Sunday services and mid-week fellowships.
4. Assign drivers to routes or schedule church community shuttles.`,
    action: { label: 'Go to Ride Connect', path: '/admin/ride-connect' },
    followUps: ['How do I add a new Sunday live stream?', 'How do I manage Events?']
  },
  {
    id: 'songs',
    keywords: ['song', 'songs', 'music', 'worship', 'chord', 'chords', 'lyrics', 'audio', 'mp3', 'hymn', 'telugu', 'tamil', 'hindi', 'pdf', 'sheet'],
    title: 'Worship Songs & Chords Library',
    text: `Under **Songs**, administrators can curate the church's Christian worship catalog:

**Features:**
• Multi-language library supporting English, Telugu, Hindi, and Tamil worship music.
• Upload MP3 audio previews and stream links.
• Provide song lyrics and downloadable PDF chord sheets with key transpositions for church musicians.
• Assign curated songs to your specific church organization.`,
    action: { label: 'Go to Songs Library', path: '/admin/songs' },
    followUps: ['How do I schedule daily devotions?', 'How do I add a new Sunday live stream?']
  },
  {
    id: 'daily_devotions',
    keywords: ['devotion', 'devotions', 'daily devotion', 'daily devotions', 'scripture', 'talk it over', 'reflection', 'tts', 'text to speech', 'daily prayer'],
    title: 'Daily Devotions & Scripture Reflections',
    text: `Author and publish daily spiritual nourishment for your congregation:

**Features:**
• Schedule daily scripture verses and devotional commentary.
• Add **"Talk It Over"** discussion questions for family and small group reflection.
• The GPN mobile app automatically delivers today's devotion with integrated **Text-to-Speech (TTS)** voice playback for members on the go.`,
    action: { label: 'Go to Daily Devotions', path: '/admin/daily-devotions' },
    followUps: ['Where is the Worship Songs library?', 'How do I manage Events?']
  },
  {
    id: 'events',
    keywords: ['event', 'events', 'calendar', 'conference', 'seminar', 'vigil', 'gathering', 'rsvp', 'retreat', 'conferences'],
    title: 'Church Events & Conferences',
    text: `Plan and broadcast church calendar events:

**Management Steps:**
1. Navigate to **Events** in the menu.
2. Click **Create Event** to input title, dates, venue/online link, and banner image.
3. Track member RSVP confirmations and attendance capacity.
4. Broadcast instant push notifications to the mobile app for upcoming prayer conferences.`,
    action: { label: 'Go to Events', path: '/admin/events' },
    followUps: ['How do I add a new Sunday live stream?', 'How do I set up a Prayer Closet initiative?']
  },
  {
    id: 'live_stream',
    keywords: ['stream', 'streaming', 'live', 'youtube', 'sunday worship', 'broadcast', 'video', 'bible study', 'live stream', 'sunday live stream'],
    title: 'Sunday Worship & Live Streaming',
    text: `To connect your church's live service directly to the mobile app:

**Configuration:**
1. Navigate to **Settings** in the left menu.
2. Open the **Sunday Worship & Bible Study** section.
3. Paste your YouTube channel or live stream embed URL.
4. Members will be able to watch your church service directly inside the GPN app with live prayer comments.`,
    action: { label: 'Go to Settings', path: '/admin/settings' },
    followUps: ['Where is the Worship Songs library?', 'How do I schedule an Event?']
  },
  {
    id: 'public_prayers',
    keywords: ['prayer', 'prayers', 'public prayer', 'public prayers', 'prayer request', 'prayer requests', 'petition', 'moderation', 'moderate'],
    title: 'Public Prayer Requests Moderation',
    text: `Review and moderate prayer requests submitted by church members:

**How to Moderate:**
1. Go to **Public Prayers** in the sidebar.
2. Review incoming prayer petitions submitted via the mobile app.
3. Filter by category (Healing, Family, Salvation, Guidance, Financial).
4. Approve requests to appear on the global or church intercessory wall, or adjust privacy settings.`,
    action: { label: 'Go to Public Prayers', path: '/admin/public-prayers' },
    followUps: ['How do I set up a Prayer Closet initiative?', 'How do I approve Testimonies?']
  },
  {
    id: 'testimonies',
    keywords: ['testimony', 'testimonies', 'praise', 'praise report', 'answered prayer', 'answered prayers', 'miracle', 'testify'],
    title: 'Answered Prayers & Testimonies',
    text: `Celebrate God's faithfulness by publishing member praise reports:

**Moderation Steps:**
1. Open **Testimonies** in the left navigation.
2. Review pending testimonies submitted by believers when prayers are answered.
3. Verify content appropriateness and click **Approve** to publish to the church praise wall.`,
    action: { label: 'Go to Testimonies', path: '/admin/testimonies' },
    followUps: ['How do I review Public Prayers?', 'What is Share & Accept and how does it work?']
  },
  {
    id: 'switch_org',
    keywords: ['switch', 'organization', 'organizations', 'org', 'campus', 'campuses', 'branch', 'branches', 'church profile', 'church logo', 'switch org'],
    title: 'Switching Organizations & Campuses',
    text: `If you manage multiple church branches or regional networks:

**How to Switch:**
• Click the **"Switch Org"** chip located beneath the organization logo on the left sidebar.
• Alternatively, click your profile avatar at the top right and select **Switch Organization**.
• Select any assigned campus to instantly toggle the active administrative context without needing to log out.`,
    action: { label: 'Go to Organizations', path: '/admin/organizations' },
    followUps: ['How do I approve or activate new users?', 'How do I complete the Admin Agreement?']
  },
  {
    id: 'guide',
    keywords: ['guide', 'help', 'tutorial', 'documentation', 'screenshots', 'screenshot', 'tour', 'walkthrough', 'how to use', 'one box in one size', 'privacy blur', 'arrows'],
    title: 'Interactive Visual Guide & Tour',
    text: `The **Admin Visual Guide** provides complete screenshot-guided tutorials for every section:

**Features:**
• **Interactive Tour View**: Master-detail flow walking step-by-step through every feature.
• **Uniform Cards Grid**: Clean, equal-height (480px) cards with directional arrow pointers and callouts.
• **Privacy Shield Toggle**: Toggle extra privacy blur or realistic ministry demo data on and off.
• Real-life annotated screenshots with callouts pointing directly to primary action controls.`,
    action: { label: 'Open Visual Guide', path: '/admin/guide' },
    followUps: ['How do I set up a Prayer Closet initiative?', 'How do I approve or activate new users?']
  },
  {
    id: 'privacy_policy',
    keywords: ['privacy', 'privacy policy', 'gdpr', 'blur', 'data', 'confidentiality', 'security', 'pii', 'fake data', 'demo data', 'privacy shield'],
    title: 'Privacy Protection & Data Security',
    text: `GPN treats all prayer requests and member personal data with strict confidentiality:

• In the Admin Portal, names and contact information can be protected with the **Privacy Shield** toggle.
• Demo mode replaces real user emails and phone numbers with sanitized ministry placeholders.
• Access our complete legal privacy policy at any time.`,
    action: { label: 'View Privacy Policy', path: '/admin/privacy-policy' },
    followUps: ['Where is the Admin Agreement?', 'Where is the Visual Guide?']
  },
  {
    id: 'terms',
    keywords: ['terms', 'terms and conditions', 'terms of service', 'legal', 'conditions', 'statement of faith'],
    title: 'Terms & Conditions',
    text: `The GPN Terms and Conditions govern the ethical and spiritual use of the platform, including acceptable use policies, church leadership standards, and intellectual property.`,
    action: { label: 'View Terms & Conditions', path: '/admin/terms-and-conditions' },
    followUps: ['Where is the Admin Agreement?', 'View Privacy Policy']
  },
  {
    id: 'roles_permissions',
    keywords: ['roles permissions', 'permissions', 'super admin role', 'church admin role', 'moderator role', 'access control'],
    title: 'Roles & Access Permissions',
    text: `Customize administrative authority across your organization:

• **Super Admin**: Complete platform oversight, organization provisioning, and compliance tracking.
• **Church Admin**: Manages local church users, prayer initiatives, songs, events, and Sunday streams.
• **Moderator**: Curates prayer requests and approves member testimonies.
• Navigate to **Roles & Permissions** in the sidebar to configure granular access rights.`,
    action: { label: 'Manage Roles & Permissions', path: '/admin/roles-permissions' },
    followUps: ['How do I approve or activate new users?', 'How do I switch organizations?']
  },
  {
    id: 'mobile_app',
    keywords: ['mobile', 'app', 'mobile app', 'ios', 'android', 'phone', 'download', 'member app'],
    title: 'GPN Mobile App Integration',
    text: `The Glocal Prayer Network mobile app connects church members worldwide on iOS and Android:

• Members receive daily devotions with text-to-speech audio.
• Congregants can submit prayer requests, subscribe to Prayer Closets, join Ride Connect carpools, and participate in Share & Accept benevolence.
• All member activity syncs in real-time with this Admin Portal.`,
    action: { label: 'Open Complete Guide', path: '/admin/guide' },
    followUps: ['How do I set up a Prayer Closet initiative?', 'How do I add a new Sunday live stream?']
  },
  {
    id: 'contact_support',
    keywords: ['contact', 'support', 'help', 'ticket', 'email', 'phone', 'technical support', 'issue', 'bug', 'feedback'],
    title: 'Contact GPN Technical Support',
    text: `Our dedicated support team is ready to assist you:

• **Direct Email**: [support@glocalprayer.net](mailto:support@glocalprayer.net)
• **Online Portal**: [glocalprayer.net/contact-us](https://glocalprayer.net/contact-us/)
• **In-App Ticket**: Switch to the **Contact Team** tab above to submit an administrative inquiry directly.`,
    action: null,
    followUps: ['Where is the Visual Guide?', 'How do I complete the Admin Agreement?']
  },
  {
    id: 'greetings',
    keywords: ['hi', 'hello', 'hey', 'good morning', 'good afternoon', 'good evening', 'greetings', 'who are you', 'what are you', 'howdy'],
    title: 'Welcome to GPN Assistant',
    text: `Hello! 👋 I am your **GPN Portal Assistant**. I provide instant guidance, how-to tutorials, and direct navigation shortcuts for all GPN administrative features.

You can ask me about:
• **Prayer Closet** & War Room initiatives
• **User approvals** & role assignments
• **Admin Agreement** compliance tracking
• **Share & Accept** benevolence hub
• **Sunday live streaming**, **Worship songs**, & **Daily devotions**

What can I assist you with today?`,
    action: { label: 'Open Visual Guide', path: '/admin/guide' },
    followUps: ['How do I set up a Prayer Closet initiative?', 'How do I approve or activate new users?']
  },
  {
    id: 'thanks',
    keywords: ['thanks', 'thank you', 'thank', 'awesome', 'great', 'helpful', 'appreciate', 'perfect'],
    title: 'You are welcome!',
    text: `You're very welcome! 🙏 It is an honor to support your ministry. If you ever need help with prayer initiatives, user approvals, or portal settings, I'm always right here. Blessings on your ministry!`,
    action: null,
    followUps: ['Open Visual Guide', 'Contact Support Team']
  }
];

// In-memory query response cache for 0ms repeat performance
const responseCache = new Map();

// Fast local intent matching engine (< 1ms execution)
const findGPNAnswer = (query) => {
  const cleanQ = query.trim().toLowerCase();
  if (!cleanQ) return null;

  // Direct greeting check
  const greetings = ['hi', 'hello', 'hey', 'good morning', 'good afternoon', 'good evening', 'greetings'];
  if (greetings.includes(cleanQ)) {
    return GPN_KNOWLEDGE_BASE.find(item => item.id === 'greetings');
  }

  // Exact prompt match check
  for (const item of GPN_KNOWLEDGE_BASE) {
    for (const kw of item.keywords) {
      if (cleanQ === kw || cleanQ === kw.toLowerCase()) {
        return item;
      }
    }
  }

  // Multi-keyword scoring
  let bestItem = null;
  let bestScore = 0;

  for (const item of GPN_KNOWLEDGE_BASE) {
    let score = 0;
    for (const kw of item.keywords) {
      if (cleanQ.includes(kw)) {
        score += kw.length > 6 ? 30 : 18;
      } else {
        const kwWords = kw.split(' ');
        for (const word of kwWords) {
          if (word.length >= 4 && cleanQ.includes(word)) {
            score += 8;
          }
        }
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestItem = item;
    }
  }

  if (bestItem && bestScore >= 12) {
    return bestItem;
  }

  return null;
};

// Ultra-fast query responder: Checks instant local intelligence first, then 2.0s bounded API fallback
const getBotResponse = async (text) => {
  const cacheKey = text.trim().toLowerCase();
  if (responseCache.has(cacheKey)) {
    return responseCache.get(cacheKey);
  }

  // 1. Instant match in local GPN Knowledge Base (Speed: ~0.1ms)
  const localMatch = findGPNAnswer(text);
  if (localMatch) {
    const result = {
      text: localMatch.text,
      action: localMatch.action,
      followUps: localMatch.followUps,
    };
    responseCache.set(cacheKey, result);
    return result;
  }

  // 2. Fast timeout-bounded external AI query (Strict 2.0s max timeout)
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);

    const res = await fetch("https://naarsoft-ai.onrender.com/mental-health/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message: text }),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (res.ok) {
      const data = await res.json();
      if (data && data.response) {
        const cleaned = data.response.replace(/<think>[\s\S]*?<\/think>/g, "").trim();
        if (cleaned) {
          const result = {
            text: cleaned,
            action: { label: 'Open Visual Guide', path: '/admin/guide' },
            followUps: ['How do I approve or activate new users?', 'How do I set up a Prayer Closet initiative?']
          };
          responseCache.set(cacheKey, result);
          return result;
        }
      }
    }
  } catch (err) {
    // Network error or 2s timeout aborted - seamlessly fall through to smart helper
  }

  // 3. Smart contextual fallback (Delivered in < 250ms)
  const fallbackResult = {
    text: `I'm here to assist you with any GPN Admin feature! Here are quick shortcuts to popular topics:

• **"How do I set up a Prayer Closet?"** — create structured prayer campaigns.
• **"How do I approve users?"** — activate new church member registrations.
• **"Admin Agreement compliance"** — track agreement signatures and certificates.
• **"What is Share & Accept?"** — benevolence and community item sharing.
• **"Sunday live streaming"** — configure YouTube worship stream embeds.

You can also browse the full visual step-by-step guide below.`,
    action: { label: 'Open Complete Visual Guide', path: '/admin/guide' },
    followUps: [
      'How do I set up a Prayer Closet initiative?',
      'How do I approve or activate new users?',
      'How do I complete the Admin Agreement?'
    ],
  };

  responseCache.set(cacheKey, fallbackResult);
  return fallbackResult;
};

// Formatted markdown renderer for clean, readable bullet points and bold highlights
const renderFormattedText = (content) => {
  if (!content) return null;
  const lines = content.split('\n');

  return lines.map((line, idx) => {
    const parts = line.split(/(\*\*.*?\*\*)/g);
    const renderedParts = parts.map((part, pIdx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={pIdx} style={{ color: '#E0F2FE', fontWeight: 700 }}>
            {part.slice(2, -2)}
          </strong>
        );
      }
      return part;
    });

    const isBullet = line.trim().startsWith('•') || line.trim().startsWith('-');
    const isNumbered = /^\d+\./.test(line.trim());

    return (
      <span
        key={idx}
        style={{
          display: 'block',
          marginBottom: line.trim() === '' ? '6px' : '2px',
          paddingLeft: isBullet || isNumbered ? '6px' : '0px',
        }}
      >
        {renderedParts}
      </span>
    );
  });
};

const SupportChatWidget = () => {
  const { user } = useUser();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  // Chat State
  const initialGreeting = {
    id: 1,
    sender: 'bot',
    text: `Hello ${user?.name ? user.name.split(' ')[0] : 'Admin'}! 👋 I'm your GPN Support Assistant. How can I assist you with the portal today?`,
    time: 'Just now',
    action: null,
    followUps: [
      'How do I set up a Prayer Closet initiative?',
      'How do I approve or activate new users?',
      'How do I complete the Admin Agreement?',
    ],
  };

  const [messages, setMessages] = useState([initialGreeting]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Contact Form State
  const [contactName, setContactName] = useState(user?.name || user?.username || '');
  const [contactEmail, setContactEmail] = useState(user?.email || '');
  const [contactCategory, setContactCategory] = useState('Feature Question');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactError, setContactError] = useState('');

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (isOpen && tabIndex === 0) {
      scrollToBottom();
    }
  }, [messages, isOpen, tabIndex, scrollToBottom]);

  // Reset conversation handler
  const handleResetChat = () => {
    setMessages([
      {
        ...initialGreeting,
        id: Date.now(),
        time: 'Just now',
      },
    ]);
  };

  // High-speed message dispatch: returns response in ~180ms - 220ms
  const handleSendMessage = async (textToSend) => {
    const text = (textToSend || inputValue).trim();
    if (!text) return;

    const userMsg = {
      id: Date.now(),
      sender: 'user',
      text,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages((prev) => [...prev, userMsg]);
    if (!textToSend) setInputValue('');
    setIsTyping(true);

    const startTime = Date.now();
    const botResponse = await getBotResponse(text);
    const elapsed = Date.now() - startTime;
    // Natural subtle delay (180ms) for snappy, realistic response without any freezing
    const remainingDelay = Math.max(0, 180 - elapsed);

    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          sender: 'bot',
          text: botResponse.text,
          action: botResponse.action,
          followUps: botResponse.followUps,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
      setIsTyping(false);
    }, remainingDelay);
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    setContactError('');

    if (!contactMessage.trim()) {
      setContactError('Please enter your support message or inquiry.');
      return;
    }

    setContactSubmitted(true);
    setContactMessage('');
  };

  return (
    <>
      {/* Floating Launcher Button */}
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 20, sm: 24 },
          right: { xs: 20, sm: 28 },
          zIndex: 1300,
        }}
      >
        <Tooltip title={isOpen ? 'Close Support' : 'Need Help? Chat & Support'} placement="left">
          <Badge
            color="success"
            variant="dot"
            overlap="circular"
            invisible={isOpen}
            sx={{
              '& .MuiBadge-badge': {
                width: 12,
                height: 12,
                borderRadius: '50%',
                border: '2px solid #1a1f2e',
              },
            }}
          >
            <IconButton
              onClick={() => setIsOpen(!isOpen)}
              sx={{
                width: 54,
                height: 54,
                borderRadius: '50%',
                background: isOpen
                  ? '#EF4444'
                  : 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                color: '#FFFFFF',
                boxShadow: '0 8px 24px rgba(76, 161, 175, 0.45)',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                '&:hover': {
                  transform: 'scale(1.08)',
                  background: isOpen
                    ? '#DC2626'
                    : 'linear-gradient(135deg, #5AB5C4 0%, #34495E 100%)',
                  boxShadow: '0 10px 28px rgba(76, 161, 175, 0.6)',
                },
              }}
            >
              {isOpen ? <CloseIcon /> : <ChatIcon />}
            </IconButton>
          </Badge>
        </Tooltip>
      </Box>

      {/* Floating Chat / Support Panel */}
      {isOpen && (
        <Paper
          elevation={0}
          className="gpn-fade-in"
          sx={{
            position: 'fixed',
            bottom: { xs: 84, sm: 90 },
            right: { xs: 16, sm: 28 },
            width: { xs: 'calc(100vw - 32px)', sm: 400 },
            maxWidth: 440,
            height: { xs: '75vh', sm: 560 },
            maxHeight: 620,
            zIndex: 1300,
            borderRadius: 3.5,
            background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%)',
            backdropFilter: 'blur(25px)',
            border: '1px solid rgba(76, 161, 175, 0.35)',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5), 0 0 25px rgba(76, 161, 175, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Top Panel Bar */}
          <Box
            sx={{
              p: 2,
              background: 'linear-gradient(135deg, #2C3E50 0%, #1e293b 100%)',
              borderBottom: '1px solid rgba(76, 161, 175, 0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                  border: '1.5px solid rgba(255, 255, 255, 0.3)',
                }}
              >
                <SmartToyIcon sx={{ fontSize: 20, color: '#FFFFFF' }} />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 700, lineHeight: 1.2 }}>
                  GPN Assistant & Support
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#10B981' }} />
                  <Typography variant="caption" sx={{ color: '#34D399', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <BoltIcon sx={{ fontSize: 13, color: '#FBBF24' }} /> Online • Instant Response
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Reset Chat">
                <IconButton
                  size="small"
                  onClick={handleResetChat}
                  sx={{ color: 'rgba(255, 255, 255, 0.6)', '&:hover': { color: '#FFFFFF' } }}
                >
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                onClick={() => setIsOpen(false)}
                sx={{ color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: '#FFFFFF' } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Navigation Tabs */}
          <Tabs
            value={tabIndex}
            onChange={(e, val) => setTabIndex(val)}
            variant="fullWidth"
            sx={{
              minHeight: 40,
              background: 'rgba(15, 23, 42, 0.6)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
              '& .MuiTab-root': {
                minHeight: 40,
                py: 1,
                fontSize: '0.8rem',
                fontWeight: 600,
                textTransform: 'none',
                color: 'rgba(255, 255, 255, 0.6)',
                '&.Mui-selected': {
                  color: '#4CA1AF',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#4CA1AF',
                height: 2.5,
              },
            }}
          >
            <Tab icon={<SmartToyIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Feature Guide" />
            <Tab icon={<SupportAgentIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Contact Team" />
          </Tabs>

          {/* Tab 0: Interactive Guide Chat */}
          {tabIndex === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              {/* Message List */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                {messages.map((msg) => (
                  <Box
                    key={msg.id}
                    sx={{
                      alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                      maxWidth: '88%',
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        background:
                          msg.sender === 'user'
                            ? 'linear-gradient(135deg, #4CA1AF 0%, #357ABD 100%)'
                            : 'rgba(255, 255, 255, 0.07)',
                        border: msg.sender === 'user' ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#FFFFFF',
                        fontSize: '0.85rem',
                        lineHeight: 1.5,
                        wordBreak: 'break-word',
                      }}
                    >
                      <Box sx={{ fontSize: '0.85rem' }}>
                        {renderFormattedText(msg.text)}
                      </Box>

                      {msg.action && (
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                          onClick={() => {
                            setIsOpen(false);
                            navigate(msg.action.path);
                          }}
                          sx={{
                            mt: 1.5,
                            color: '#6DD5FA',
                            borderColor: 'rgba(109, 213, 250, 0.5)',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            py: 0.4,
                            px: 1.2,
                            borderRadius: 1.5,
                            '&:hover': {
                              background: 'rgba(109, 213, 250, 0.15)',
                              borderColor: '#6DD5FA',
                            },
                          }}
                        >
                          {msg.action.label}
                        </Button>
                      )}

                      {/* Suggested Follow-up chips */}
                      {msg.followUps && msg.followUps.length > 0 && (
                        <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.68rem', display: 'block', mb: 0.6 }}>
                            Suggested next:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                            {msg.followUps.map((prompt, pIdx) => (
                              <Chip
                                key={pIdx}
                                label={prompt}
                                size="small"
                                onClick={() => handleSendMessage(prompt)}
                                sx={{
                                  fontSize: '0.7rem',
                                  height: 22,
                                  background: 'rgba(76, 161, 175, 0.12)',
                                  border: '1px solid rgba(76, 161, 175, 0.3)',
                                  color: '#E0F2FE',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    background: 'rgba(76, 161, 175, 0.25)',
                                    borderColor: '#4CA1AF',
                                  },
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.4)',
                        fontSize: '0.65rem',
                        display: 'block',
                        mt: 0.3,
                        textAlign: msg.sender === 'user' ? 'right' : 'left',
                        px: 0.5,
                      }}
                    >
                      {msg.time}
                    </Typography>
                  </Box>
                ))}

                {isTyping && (
                  <Box sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                    <CircularProgress size={14} sx={{ color: '#4CA1AF' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                      GPN Assistant is finding answers...
                    </Typography>
                  </Box>
                )}

                <div ref={messagesEndRef} />
              </Box>

              {/* Quick Prompts Carousel */}
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  display: 'flex',
                  gap: 1,
                  overflowX: 'auto',
                  borderTop: '1px solid rgba(255, 255, 255, 0.06)',
                  background: 'rgba(15, 23, 42, 0.4)',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                {quickPrompts.map((prompt, idx) => (
                  <Chip
                    key={idx}
                    label={prompt}
                    size="small"
                    onClick={() => handleSendMessage(prompt)}
                    sx={{
                      fontSize: '0.72rem',
                      height: 24,
                      background: 'rgba(76, 161, 175, 0.12)',
                      border: '1px solid rgba(76, 161, 175, 0.3)',
                      color: '#E0F2FE',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      '&:hover': {
                        background: 'rgba(76, 161, 175, 0.25)',
                        borderColor: '#4CA1AF',
                      },
                    }}
                  />
                ))}
              </Box>

              {/* Chat Input */}
              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendMessage();
                }}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  background: 'rgba(15, 23, 42, 0.8)',
                  borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Ask any question about GPN features..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  sx={{
                    '& .MuiInputBase-input': { color: '#FFFFFF', fontSize: '0.85rem', py: 0.8 },
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 2,
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                      '&:hover fieldset': { borderColor: '#4CA1AF' },
                      '&.Mui-focused fieldset': { borderColor: '#4CA1AF' },
                    },
                  }}
                />
                <IconButton
                  type="submit"
                  disabled={!inputValue.trim()}
                  sx={{
                    background: inputValue.trim() ? '#4CA1AF' : 'rgba(255, 255, 255, 0.1)',
                    color: '#FFFFFF',
                    width: 36,
                    height: 36,
                    '&:hover': { background: '#357ABD' },
                  }}
                >
                  <SendIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* Tab 1: Contact Team */}
          {tabIndex === 1 && (
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {/* Direct Contacts Info */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  background: 'rgba(76, 161, 175, 0.1)',
                  border: '1px solid rgba(76, 161, 175, 0.3)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <EmailIcon sx={{ color: '#4CA1AF', fontSize: 20 }} />
                  <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
                    Official GPN Support
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8rem', mb: 1 }}>
                  Direct Email:{' '}
                  <a href="mailto:support@glocalprayer.net" style={{ color: '#6DD5FA', fontWeight: 600 }}>
                    support@glocalprayer.net
                  </a>
                </Typography>
                <Button
                  size="small"
                  endIcon={<OpenInNewIcon />}
                  component="a"
                  href="https://glocalprayer.net/contact-us/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    p: 0,
                    color: '#4CA1AF',
                    textTransform: 'none',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                  }}
                >
                  Visit Contact Page (glocalprayer.net)
                </Button>
              </Box>

              {/* Inquiry Form */}
              {contactSubmitted ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CheckCircleIcon sx={{ color: '#10B981', fontSize: 48, mb: 1.5 }} />
                  <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
                    Message Received!
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem', mt: 1, mb: 3 }}>
                    Thank you for reaching out. The GPN Technical Support team will respond to your registered email shortly.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setContactSubmitted(false)}
                    sx={{
                      color: '#4CA1AF',
                      borderColor: '#4CA1AF',
                      textTransform: 'none',
                      fontSize: '0.8rem',
                    }}
                  >
                    Send Another Message
                  </Button>
                </Box>
              ) : (
                <Box component="form" onSubmit={handleContactSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
                    Submit an Administrative Support Ticket
                  </Typography>

                  {contactError && (
                    <Alert severity="error" sx={{ py: 0.5, fontSize: '0.8rem' }}>
                      {contactError}
                    </Alert>
                  )}

                  <TextField
                    size="small"
                    label="Your Name"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    sx={{
                      '& .MuiInputBase-input': { color: '#FFFFFF', fontSize: '0.85rem' },
                      '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' },
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(255, 255, 255, 0.04)',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                      },
                    }}
                  />

                  <TextField
                    size="small"
                    label="Email Address"
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    sx={{
                      '& .MuiInputBase-input': { color: '#FFFFFF', fontSize: '0.85rem' },
                      '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' },
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(255, 255, 255, 0.04)',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                      },
                    }}
                  />

                  <TextField
                    size="small"
                    select
                    SelectProps={{ native: true }}
                    label="Inquiry Category"
                    value={contactCategory}
                    onChange={(e) => setContactCategory(e.target.value)}
                    sx={{
                      '& .MuiInputBase-input': { color: '#FFFFFF', fontSize: '0.85rem' },
                      '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' },
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(255, 255, 255, 0.04)',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                      },
                    }}
                  >
                    <option value="Feature Question" style={{ background: '#1e293b', color: '#fff' }}>Feature Guidance / How-To</option>
                    <option value="Technical Issue" style={{ background: '#1e293b', color: '#fff' }}>Technical Bug / Error</option>
                    <option value="Account & Roles" style={{ background: '#1e293b', color: '#fff' }}>Account & Permissions</option>
                    <option value="Organization Setup" style={{ background: '#1e293b', color: '#fff' }}>Organization Setup</option>
                    <option value="Admin Agreement" style={{ background: '#1e293b', color: '#fff' }}>Admin Agreement Help</option>
                  </TextField>

                  <TextField
                    multiline
                    rows={3}
                    placeholder="Describe how we can assist you..."
                    value={contactMessage}
                    onChange={(e) => setContactMessage(e.target.value)}
                    sx={{
                      '& .MuiInputBase-input': { color: '#FFFFFF', fontSize: '0.85rem' },
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(255, 255, 255, 0.04)',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                        '&:hover fieldset': { borderColor: '#4CA1AF' },
                      },
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SendIcon />}
                    sx={{
                      mt: 1,
                      background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      py: 1,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5AB5C4 0%, #34495E 100%)',
                      },
                    }}
                  >
                    Send Support Request
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      )}
    </>
  );
};

export default SupportChatWidget;
